/**
 * LanguageSwitcher — top-nav dropdown that lets the user pick UI language.
 *
 * Phase 7 §C: Rafael's feedback was that the app auto-translated to Spanish
 * for users on a Spanish-locale browser. Resolution: English is the default
 * (i18n config no longer reads navigator), and language is changed explicitly
 * via this dropdown in the AppShell top-nav.
 *
 * On change:
 *  1. `i18n.changeLanguage(locale)` updates the live language
 *  2. localStorage[insolech-locale] is cached automatically by i18next
 *  3. If signed in, persist to `profiles.language_preference` (best-effort —
 *     RLS already allows users to update their own profile row)
 *  4. `useDirectionEffect` (mounted in App.tsx) watches `i18n.language` and
 *     flips `<html dir>` for RTL when needed — no extra wiring required here.
 *
 * a11y:
 *  - Native <select> for keyboard + screen-reader handling out of the box
 *  - Globe icon as the visual affordance, hidden via aria-hidden
 *  - Visible label "Language" hidden visually but available to AT
 */

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import { LOCALES, LOCALE_LABELS, type Locale } from '@insolech/i18n';
import { Tooltip } from '@insolech/ui';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth/AuthContext';

export function LanguageSwitcher() {
  const { i18n, t } = useTranslation('common');
  const { user } = useAuth();
  const current = (LOCALES as readonly string[]).includes(i18n.language)
    ? (i18n.language as Locale)
    : 'en';

  const onChange = React.useCallback(
    async (next: Locale) => {
      await i18n.changeLanguage(next);
      // Best-effort: persist to profile when signed in. RLS allows
      // self-update on profiles. If it fails (offline, no row yet), the
      // localStorage cache (set automatically by i18next) is enough to
      // restore the choice on next mount.
      if (user?.id) {
        try {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          await (supabase.from('profiles') as any)
            .update({ language_preference: next })
            .eq('id', user.id);
        } catch (err) {
          console.warn('[LanguageSwitcher] failed to persist language_preference', err);
        }
      }
    },
    [i18n, user?.id],
  );

  return (
    <Tooltip content={t('language', { defaultValue: 'Language' })}>
      <label className="relative inline-flex items-center">
        <span className="sr-only">{t('language', { defaultValue: 'Language' })}</span>
        <Globe
          aria-hidden="true"
          className="absolute start-2 top-1/2 -translate-y-1/2 h-4 w-4 text-text-muted pointer-events-none"
        />
        <select
          aria-label={t('language', { defaultValue: 'Language' })}
          data-testid="language-switcher"
          value={current}
          onChange={(e) => void onChange(e.target.value as Locale)}
          className="h-9 max-sm:h-11 ps-8 pe-7 rounded-md bg-transparent border border-transparent hover:bg-sand-100 text-base sm:text-sm text-text-secondary hover:text-text-primary focus-visible:outline-none focus-visible:border-border-focus appearance-none cursor-pointer"
        >
          {LOCALES.map((loc) => (
            <option key={loc} value={loc}>
              {LOCALE_LABELS[loc]}
            </option>
          ))}
        </select>
      </label>
    </Tooltip>
  );
}
